import { Text } from '@spotify-internal/encore-web';

import { ToolGroupEntry } from '../../extensions/tool-extension';
import { useToolExtensions } from '../../extensions/tool-extension';
import { ChatInformationBox } from '../ChatInformationBox';
import { formatDefaultToolLabel, inferToolIcon } from './defaultToolMappings';
import { sharedToolStyles } from './sharedStyles';

import styles from './DefaultToolCallGroup.module.css';

const ToolEntryLabel = ({
  entry,
  iconSize = 14,
}: {
  entry: ToolGroupEntry;
  iconSize?: number;
}) => {
  const toolExtensions = useToolExtensions();
  const ext = toolExtensions.find(e => e.predicate(entry.tool));

  const IconComponent = ext?.icon ?? inferToolIcon(entry.tool);

  if (ext?.renderLabel) {
    return (
      <>
        <IconComponent size={iconSize} className={sharedToolStyles.icon} />
        {ext.renderLabel(entry)}
      </>
    );
  }

  const label = ext?.formatLabel
    ? ext.formatLabel(entry)
    : formatDefaultToolLabel(entry);

  return (
    <>
      <IconComponent size={iconSize} className={sharedToolStyles.icon} />
      <Text variant="bodySmall" className={styles.text}>
        {label.text}
        {label.detail && (
          <span className={styles.text}>{` ${label.detail}`}</span>
        )}
      </Text>
    </>
  );
};

export const DefaultToolCallGroup = ({
  entries,
}: {
  entries: ToolGroupEntry[];
}) => {
  const toolExtensions = useToolExtensions();

  if (entries.length === 1) {
    const entry = entries[0];
    const ext = toolExtensions.find(e => e.predicate(entry.tool));

    if (ext?.display === 'prominent' && ext.renderTool) {
      const renderedTool = ext.renderTool(entry);
      if (renderedTool) {
        return <>{renderedTool}</>;
      }
      return (
        <div className={styles.listItem}>
          <ToolEntryLabel entry={entry} iconSize={12} />
        </div>
      );
    }

    return <ChatInformationBox title={<ToolEntryLabel entry={entry} />} />;
  }

  return (
    <div className={styles.list}>
      {entries.map((entry, i) => {
        const ext = toolExtensions.find(e => e.predicate(entry.tool));

        if (ext?.display === 'prominent' && ext.renderTool) {
          const renderedTool = ext.renderTool(entry);
          if (!renderedTool) {
            return (
              <div key={i} className={styles.listItem}>
                <ToolEntryLabel entry={entry} iconSize={12} />
              </div>
            );
          }

          return <div key={i}>{renderedTool}</div>;
        }

        return (
          <div key={i} className={styles.listItem}>
            <ToolEntryLabel entry={entry} iconSize={12} />
          </div>
        );
      })}
    </div>
  );
};
